import { useMemo } from "react";
import { useDateRange } from "@shared/hooks/useDateRange";
import { DateRangeFilter } from "@shared/ui/DateRangeFilter";
import { useEvaluations } from "@features/evaluation/queries";
import { exportToExcel } from "@shared/utils/excel-export";
import { computeKpis } from "@features/dashboard/kpis";
import { today } from "@shared/utils/date";
import { HttpError } from "@shared/http/client";
import { useAuth } from "@features/auth/AuthContext";
import { useNotice } from "@shared/ui/NoticeDialog";

type PenalizacionRow = {
  Fecha: string;
  Programador: string;
  Paciente: string;
  Error: string;
  Nota_Evaluador: string;
  Impacto: string;
};

export function InformesPage() {
  const { user } = useAuth();
  const { showInfo } = useNotice();
  const { range } = useDateRange();
  const { data, isLoading, error } = useEvaluations(range);
  const list = data?.items ?? [];
  const kpis = useMemo(() => computeKpis(list), [list]);

  const onExportGeneral = () => {
    if (list.length === 0) return;
    void exportToExcel(
      list,
      [
        { key: "doc",                  label: "Documento" },
        { key: "nombre",               label: "Nombre" },
        { key: "cirugia",              label: "Cirugía" },
        { key: "fecha",                label: "Fecha programada" },
        { key: "fechaRegistro",        label: "Fecha registro" },
        { key: "telefono",             label: "Teléfono" },
        { key: "programador",          label: "Programador" },
        { key: "evaluadorNombre",      label: "Evaluador" },
        { key: "diagnostico",          label: "Diagnóstico" },
        { key: "cie10",                label: "CIE-10" },
        { key: "estadoAdministrativo", label: "Estado administrativo" },
        { key: "resultadoClinico",     label: "Resultado clínico" },
        { key: "accionFinal",          label: "Decisión final" },
      ],
      `informe-general-${today()}`,
      {
        reportTitle: "FOCA — Informe general",
        rangeFrom: range.from,
        rangeTo: range.to,
        generatedBy: user?.name || user?.username || "",
      }
    );
  };

  const onExportPenalizaciones = () => {
    const penalizaciones: PenalizacionRow[] = [];
    for (const item of list) {
      const rawAlerts = (item as { alertasAdministrativas?: unknown }).alertasAdministrativas;
      const alerts = Array.isArray(rawAlerts) ? rawAlerts as Array<{ penalizaProgramador?: boolean; titulo?: string }> : [];
      for (const a of alerts) {
        if (a && a.penalizaProgramador) {
          penalizaciones.push({
            Fecha:          item.fechaRegistro || "",
            Programador:    item.programador || "",
            Paciente:       item.nombre || "",
            Error:          a.titulo || "",
            Nota_Evaluador: String((item as { observaciones?: unknown }).observaciones || ""),
            Impacto:        "PENALIZABLE",
          });
        }
      }
    }
    if (penalizaciones.length === 0) {
      showInfo("Sin penalizaciones", "No hay errores penalizables en el rango seleccionado.");
      return;
    }
    void exportToExcel(
      penalizaciones,
      [
        { key: "Fecha",          label: "Fecha" },
        { key: "Programador",    label: "Programador" },
        { key: "Paciente",       label: "Paciente" },
        { key: "Error",          label: "Error" },
        { key: "Nota_Evaluador", label: "Nota del evaluador" },
        { key: "Impacto",        label: "Impacto" },
      ],
      `penalizaciones-${today()}`,
      {
        reportTitle: "FOCA — Reporte de penalizaciones (errores administrativos por programador)",
        rangeFrom: range.from,
        rangeTo: range.to,
        generatedBy: user?.name || user?.username || "",
      }
    );
  };

  return (
    <div className="p-24">
      <div className="page-header">
        <h1>Informes consolidados</h1>
        <p>Informes exportables en Excel a partir de las valoraciones registradas.</p>
      </div>

      <div className="mb-16">
        <DateRangeFilter />
      </div>

      {error instanceof HttpError && (
        <div className="alert-inline alert-inline--danger mt-16">Error cargando datos: {error.message}</div>
      )}

      <div className="kpi-grid mt-16">
        <div className="kpi-card kpi-card--info">
          <div className="kpi-card-label">Total</div>
          <div className="kpi-card-value">{kpis.total}</div>
        </div>
        <div className="kpi-card kpi-card--success">
          <div className="kpi-card-label">Aptos</div>
          <div className="kpi-card-value">{kpis.aptos}</div>
        </div>
        <div className="kpi-card kpi-card--warn">
          <div className="kpi-card-label">En gestión</div>
          <div className="kpi-card-value">{kpis.enGestion}</div>
        </div>
        <div className="kpi-card kpi-card--warn">
          <div className="kpi-card-label">Reprogramados</div>
          <div className="kpi-card-value">{kpis.reprogramados}</div>
        </div>
        <div className="kpi-card kpi-card--danger">
          <div className="kpi-card-label">No aptos</div>
          <div className="kpi-card-value">{kpis.noAptos}</div>
        </div>
      </div>

      <div className="informe-grid mt-24">
        <button type="button" className="informe-btn" onClick={onExportGeneral} disabled={isLoading || list.length === 0}>
          <div className="icon">📊</div>
          <div className="label">Informe General</div>
          <div className="sub">Todas las valoraciones del rango — Excel completo con {list.length} filas</div>
        </button>

        <button type="button" className="informe-btn informe-btn--danger" onClick={onExportPenalizaciones} disabled={isLoading || list.length === 0}>
          <div className="icon">🚨</div>
          <div className="label">Reporte de Penalizaciones</div>
          <div className="sub">Errores administrativos por programador (alertas con impacto penalizable)</div>
        </button>
      </div>

      {isLoading && <div className="text-muted mt-16">Cargando…</div>}
    </div>
  );
}
